import { useMemo, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { Button } from "../ui/button";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { filterArticles } from "../../lib/pickerSearch";

const TYPE_LABEL = { bulk: "Menge", single: "Gerät" };

/**
 * ArticlePicker -- suchbare Combobox für Artikel (Stammdaten), z. B. im
 * Wareneingang, wo es noch keine Bestands-Position gibt (#182).
 *
 * Suche wie im PositionPicker (#174): jedes Wort muss in Name, EAN oder
 * Hersteller-Nr. vorkommen, Reihenfolge egal; Treffer alphabetisch.
 *
 * Props:
 *   value, onChange, articles[] (Array), placeholder, label, testId
 */

export function ArticlePicker({
    value,
    onChange,
    articles,
    placeholder = "Artikel wählen",
    label = "Artikel",
    testId = "article-picker",
    emptyLabel = "Kein passender Artikel.",
}) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");

    const current = useMemo(() => articles.find((a) => a.id === value) || null, [articles, value]);
    const filtered = useMemo(() => filterArticles(articles, search), [articles, search]);

    const triggerLabel = current
        ? `${current.name} (${TYPE_LABEL[current.type] || current.type})`
        : placeholder;
    const countLabel = filtered.length === 1 ? "1 Treffer" : `${filtered.length} Treffer`;

    return (
        <Popover
            open={open}
            onOpenChange={(next) => {
                setOpen(next);
                if (!next) setSearch("");
            }}
        >
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    aria-label={current ? `${label}: ${triggerLabel}` : label}
                    className="mb-2 h-11 w-full justify-between text-left font-normal"
                    data-testid={testId}
                >
                    <span className={`truncate ${current ? "text-foreground" : "text-muted-foreground"}`}>
                        {triggerLabel}
                    </span>
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className="w-[--radix-popover-trigger-width] p-0"
                data-testid={`${testId}-popover`}
            >
                <Command shouldFilter={false}>
                    <CommandInput
                        placeholder="Name, EAN oder Hersteller-Nr. suchen..."
                        className="h-10"
                        value={search}
                        onValueChange={setSearch}
                        aria-label="Artikel nach Name, EAN oder Hersteller-Nr. suchen"
                    />
                    <div
                        className="border-b border-border/60 px-3 py-1 text-xs text-foreground/70"
                        aria-live="polite"
                        data-testid={`${testId}-count`}
                    >
                        {countLabel}
                    </div>
                    <CommandList className="max-h-72">
                        <CommandEmpty>{emptyLabel}</CommandEmpty>
                        <CommandGroup>
                            {filtered.map((a) => {
                                const meta = [
                                    TYPE_LABEL[a.type] || a.type,
                                    a.ean ? `EAN ${a.ean}` : null,
                                    a.manufacturer_number ? `HNr ${a.manufacturer_number}` : null,
                                ].filter(Boolean);
                                return (
                                    <CommandItem
                                        key={a.id}
                                        value={a.id}
                                        onSelect={() => {
                                            onChange(a.id);
                                            setOpen(false);
                                            setSearch("");
                                        }}
                                        data-testid={`${testId}-item-${a.id}`}
                                        className="flex items-start gap-2 py-2"
                                    >
                                        <Check
                                            className={`mt-1 h-4 w-4 ${value === a.id ? "opacity-100" : "opacity-0"}`}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="truncate text-sm font-medium">{a.name}</div>
                                            <div className="truncate text-xs text-foreground/70">
                                                {meta.join(" · ")}
                                            </div>
                                        </div>
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
